import { createConfig, http, fallback } from 'wagmi';
import { mainnet, sepolia, polygon, polygonAmoy } from 'wagmi/chains';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';

// Get RPC URLs from environment or use public endpoints
const getRpcUrl = (chainId, defaultPublicRpc) => {
  const envVar = chainId === 1 
    ? import.meta.env.VITE_ETHEREUM_RPC_URL 
    : import.meta.env.VITE_SEPOLIA_RPC_URL;
  
  return envVar || defaultPublicRpc;
};

// Public RPC endpoints as fallback
const publicRpcEndpoints = {
  [mainnet.id]: [
    'https://eth.llamarpc.com',
    'https://rpc.ankr.com/eth',
    'https://ethereum.publicnode.com',
  ],
  [sepolia.id]: [
    'https://rpc.sepolia.org',
    'https://sepolia.infura.io/v3/9aa3d95b3bc440fa88ea12eaa4456161', // Public Infura endpoint
  ],
  [polygon.id]: [
    'https://polygon-rpc.com',
    'https://rpc.ankr.com/polygon',
    'https://polygon.llamarpc.com',
  ],
  [polygonAmoy.id]: [
    'https://rpc-amoy.polygon.technology',
    'https://polygon-amoy.drpc.org',
  ],
};

// Build transport configuration
const buildTransports = () => {
  const transports = {};
  
  // Mainnet
  const mainnetRpc = getRpcUrl(1, publicRpcEndpoints[mainnet.id][0]);
  const mainnetEndpoints = mainnetRpc 
    ? [http(mainnetRpc), ...publicRpcEndpoints[mainnet.id].map(url => http(url))]
    : publicRpcEndpoints[mainnet.id].map(url => http(url));
  
  transports[mainnet.id] = fallback(mainnetEndpoints, { rank: false });
  
  // Sepolia
  const sepoliaRpc = getRpcUrl(11155111, publicRpcEndpoints[sepolia.id][0]);
  const sepoliaEndpoints = sepoliaRpc
    ? [http(sepoliaRpc), ...publicRpcEndpoints[sepolia.id].map(url => http(url))]
    : publicRpcEndpoints[sepolia.id].map(url => http(url));
  
  transports[sepolia.id] = fallback(sepoliaEndpoints, { rank: false });
  
  // Polygon
  transports[polygon.id] = fallback(
    publicRpcEndpoints[polygon.id].map(url => http(url)),
    { rank: false }
  );
  
  // Polygon Amoy (ForgeID)
  transports[polygonAmoy.id] = fallback(
    publicRpcEndpoints[polygonAmoy.id].map(url => http(url)),
    { rank: false }
  );
  
  return transports;
};

// Create wagmi config with Reown AppKit
// Note: projectId is required for Reown AppKit
const reownProjectId = import.meta.env.VITE_REOWN_PROJECT_ID;

if (!reownProjectId || reownProjectId === 'YOUR_PROJECT_ID') {
  console.warn('⚠️ VITE_REOWN_PROJECT_ID not set. Get your Project ID from https://cloud.reown.com');
}

// Ensure chains are valid and have corresponding transports
const chains = [mainnet, sepolia, polygon, polygonAmoy].filter(Boolean); // Filter out any undefined chains
const transports = buildTransports();

// Validate that all chains have transports
chains.forEach(chain => {
  if (!transports[chain.id]) {
    console.warn(`⚠️ No transport configured for chain ${chain.id} (${chain.name})`);
  }
});

// Create wagmi adapter with proper configuration
// Note: WagmiAdapter requires 'networks' parameter (can use chains as they extend BaseNetwork)
// The adapter will filter networks to get EVM chains internally
const wagmiAdapterConfig = {
  chains, // For wagmi config creation
  networks: chains, // Required by WagmiAdapter - chains work as BaseNetwork
  transports,
  projectId: reownProjectId || 'YOUR_PROJECT_ID', // Required for Reown AppKit
  ssr: false, // Client-side only (not server-side rendered)
  // Disable auto-connect - wagmi v2 handles this via storage configuration
  // We'll configure storage to prevent auto-connect in the config itself
};

// Validate configuration before creating adapter
if (!wagmiAdapterConfig.chains || wagmiAdapterConfig.chains.length === 0) {
  throw new Error('At least one chain must be configured');
}

if (!wagmiAdapterConfig.projectId || wagmiAdapterConfig.projectId === 'YOUR_PROJECT_ID') {
  console.warn('⚠️ Using placeholder project ID. Get your Project ID from https://cloud.reown.com');
}

const wagmiAdapter = new WagmiAdapter(wagmiAdapterConfig);

export const config = wagmiAdapter.wagmiConfig;

// Export adapter and networks for AppKit
export { wagmiAdapter };
export { chains as networks }; // Export networks for AppKitProvider
